export const GOALS = [
  "Just Invest",
  "Retirement",
  "Major Purchase",
  "Supplement Income",
  "High Yield Cash",
] as const;

export const ALLOCATIONS = [
  "Growth",
  "Capital Preservation",
  "Yield",
  "Money Market",
] as const;

export const RETIREMENT_TYPES = [
  "ROTH",
  "TRADITIONAL",
  "ROLLOVER",
  "TAXABLE",
] as const;

export type GoalType = (typeof GOALS)[number];

export type AllocationType = (typeof ALLOCATIONS)[number];

export type RetirementType = (typeof RETIREMENT_TYPES)[number];

export const COLORSCHEME: Record<
  string | "Overall",
  Record<AllocationType, string>
> = {
  "Just Invest": {
    Growth: "#4F68E4",
    "Capital Preservation": "#3A4384",
    Yield: "#808DE1",
    "Money Market": "#9CA3AF",
  },
  "High Yield Cash": {
    Growth: "#dc395c",
    "Capital Preservation": "#732d38",
    Yield: "#f7a3ab",
    "Money Market": "#9CA3AF",
  },
  Retirement: {
    Growth: "#D4C087",
    "Capital Preservation": "#C79F35",
    Yield: "#E5B01F",
    "Money Market": "#9CA3AF",
  },
  "Major Purchase": {
    Growth: "#E49B7A",
    "Capital Preservation": "#DF5C22",
    Yield: "#E9875B",
    "Money Market": "#9CA3AF",
  },
  "Supplement Income": {
    Growth: "#FFFF8B",
    "Capital Preservation": "#F6F625",
    Yield: "#FFFFDD",
    "Money Market": "#9CA3AF",
  },
  Overall: {
    Growth: "#856095",
    "Capital Preservation": "#6F3479",
    Yield: "#BDA1C9",
    "Money Market": "#9CA3AF",
  },
};

export type UserInfo = {
  retirementAge: number;
  longevity: number;
  married: boolean;
  annualIncome: number;
  monthlyExpenses: number;
  totalSavings: number;
  liquidNetWorth: number;
  annualWithdrawal: number;
  house: boolean;
  houseValue?: number;
  mortgageRemaining?: number;
};

export type Portfolio = {
  totalInvested: number;
  allocations: Partial<Record<AllocationType, number>>;
  timeHorizon: number;
  retirementTypes?: RetirementType[];
};

export type IncomeExpenseProjection = {
  year: number;
  income: number;
  expenses: number;
  netSavings: number;
  totalSavings: number;
};

export type ProjectedSaving = {
  year: number;
  percentile_5: number;
  percentile_25: number;
  percentile_50: number;
  percentile_75: number;
  percentile_95: number;
};

export type HomePriceProjection = {
  year: number;
  projectionValue: number;
};

export type FinancialPlansState = PortfolioState[];

export type PortfolioState = {
  title?: string;
  userInfo: UserInfo;
  portfolios: Partial<Record<GoalType, Portfolio>>;
  crisisDiagram?: number[];
  monteCarloSimulation?: [number[], number[], number[]];
  incomeExpenseProjection?: IncomeExpenseProjection[];
  projectionSavings?: ProjectedSaving[];
  homePriceProjection?: HomePriceProjection[];
  probabilityOfSuccess?: number;
  scenarioTimestamp?: string;
  scenarioId?: string;
};

export function entries<K extends string | number | symbol, V>(
  object: Record<K, V> | Partial<Record<K, V>>,
): [K, V][] {
  return Object.entries(object) as [K, V][];
}
